import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 12_000,
});

api.interceptors.request.use((config) => {
  config.headers['apikey']     = process.env.REACT_APP_API_KEY;
  config.headers['appversion'] = process.env.REACT_APP_VERSION;
  return config;
});

let _authToken = null;
export function setAuthToken(token) { _authToken = token; }
export function clearAuthToken()    { _authToken = null; }
export function getAuthToken()      { return _authToken; }

function optionalAuthHeader() {
  return _authToken ? { authorization: _authToken } : {};
}
function authHeader() {
  if (!_authToken) throw new Error('Not authenticated');
  return { authorization: _authToken };
}

// "_reel" catalog entries (e.g. filmid "kannaal_reel") are short-form clips
// meant for the mobile app only — the website has no reel-viewing UI for them,
// so they render as broken/blank cards wherever they leak into a movie list
// (Continue Watching, home rows, search). Strip them out at the API layer so
// every consumer of these endpoints gets clean data automatically.
function isReelItem(m) {
  const id = String(m?.filmid ?? m?.id ?? m?.programid ?? '');
  return /_reel$/i.test(id);
}
function stripReelsFromRows(rows) {
  if (!Array.isArray(rows)) return rows;
  return rows.map(row => {
    if (Array.isArray(row?.movies))    return { ...row, movies: row.movies.filter(m => !isReelItem(m)) };
    if (Array.isArray(row?.movieList)) return { ...row, movieList: row.movieList.filter(m => !isReelItem(m)) };
    return row;
  });
}
function stripReelsFromList(list) {
  return Array.isArray(list) ? list.filter(m => !isReelItem(m)) : list;
}

// ─────────────────────────────────────────────────────────────────────────────
// ENDPOINT STRATEGY
// Every public endpoint has two server-side routes:
//   /mobile  → requires Firebase token (authToken.verifyUser)
//   /tv      → requires API key ONLY  (no Firebase needed)
//
// Anonymous sign-in is DISABLED in this Firebase project.
// So for guest users (no token) we always use the /tv route.
// For logged-in users we use the /mobile route (returns personalised data).
// This pattern applies to: banner, sflist, detail, episodes, search.
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchMovieBanner() {
  if (_authToken) {
    const { data } = await api.get('movies/banner/mobile', { headers: { authorization: _authToken } });
    return { ...data, data: stripReelsFromRows(data?.data) };
  }
  const { data } = await api.get('movies/banner/tv');
  return { ...data, data: stripReelsFromRows(data?.data) };
}

export async function fetchMovieList() {
  if (_authToken) {
    const { data } = await api.get('movies/sflist/mobile', { headers: { authorization: _authToken } });
    return { ...data, data: stripReelsFromRows(data?.data) };
  }
  const { data } = await api.get('movies/sflist/tv');
  return { ...data, data: stripReelsFromRows(data?.data) };
}

export async function fetchProgramDetail(id) {
  if (_authToken) {
    const { data } = await api.get(`movie/mobile/${id}`, { headers: { authorization: _authToken } });
    return data;
  }
  const { data } = await api.get(`movie/tv/${id}`);
  return data;
}

export async function fetchEpisodeList(id) {
  if (_authToken) {
    const { data } = await api.get(`movie/mobile/${id}/episodes`, { headers: { authorization: _authToken } });
    return data;
  }
  const { data } = await api.get(`movie/tv/${id}/episodes`);
  return data;
}

export async function fetchSearchList(searchText) {
  if (_authToken) {
    const { data } = await api.get(
      `movies/search/mobile/${encodeURIComponent(searchText)}`,
      { headers: { authorization: _authToken } }
    );
    return { ...data, data: stripReelsFromList(data?.data) };
  }
  const { data } = await api.get(`movies/search/tv/${encodeURIComponent(searchText)}`);
  return { ...data, data: stripReelsFromList(data?.data) };
}

export async function fetchGeneralMessage() {
  if (_authToken) {
    const { data } = await api.get('startmessage/Mobile-Android', { headers: { authorization: _authToken } });
    return data?.data?.[0] ?? null;
  }
  const { data } = await api.get('startmessage/tv');
  return data?.data?.[0] ?? null;
}

// ── Auth-required endpoints ───────────────────────────────────────────────────
export async function addToWatchlist(filmId) {
  const params = new URLSearchParams({ filmid: filmId });
  const { data } = await api.post('favlist', params, {
    headers: { ...authHeader(), 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  return data;
}

export async function removeFromWatchlist(filmId) {
  const params = new URLSearchParams({ filmid: filmId });
  const { data } = await api.delete('favlist', {
    headers: { ...authHeader(), 'Content-Type': 'application/x-www-form-urlencoded' },
    data: params,
  });
  return data;
}

export async function getWatchList() {
  const { data } = await api.get('favlist', { headers: authHeader() });
  return { ...data, data: stripReelsFromList(data?.data) };
}

// ── Watch progress events ─────────────────────────────────────────────────────
// Backend events: play_video | pause_video | stop_video | end_video
// Body fields: filmid, episodeid (optional), position (seconds), completed (0|1)
export async function completionEvent(event, { filmId, episodeId, position, completed = 0 }) {
  const params = new URLSearchParams({
    filmid:    filmId,
    episodeid: episodeId || '',
    position:  String(Math.floor(position || 0)),
    completed: String(completed),
  });
  await api.put(`activity/${event}`, params, {
    headers: { ...authHeader(), 'Content-Type': 'application/x-www-form-urlencoded' },
  });
}

// Same PUT as completionEvent, but fire-and-forget via fetch's `keepalive`
// flag instead of axios. A normal async request gets cancelled the instant
// the tab closes/refreshes or the browser backgrounds the app — keepalive
// requests are explicitly allowed to keep going after the page is gone
// (unlike navigator.sendBeacon, fetch+keepalive still lets us set the
// apikey/authorization headers and use PUT). Call this from pagehide /
// visibilitychange handlers to catch exits that never let a normal cleanup
// effect finish — this is best-effort: still not 100% guaranteed by spec,
// but far more reliable than a plain axios call during unload.
export function completionEventBeacon(event, { filmId, episodeId, position, completed = 0 }) {
  if (!_authToken) return;
  const params = new URLSearchParams({
    filmid:    filmId,
    episodeid: episodeId || '',
    position:  String(Math.floor(position || 0)),
    completed: String(completed),
  });
  try {
    fetch(`${api.defaults.baseURL}/activity/${event}`, {
      method: 'PUT',
      keepalive: true,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        apikey:        process.env.REACT_APP_API_KEY,
        appversion:    process.env.REACT_APP_VERSION,
        authorization: _authToken,
      },
      body: params,
    }).catch(() => {});
  } catch (_) {}
}

export async function validateMembership() {
  const { data } = await api.get('membership/validate', { headers: authHeader() });
  return data;
}

export async function fetchSubscriptionPlans() {
  const { data } = await api.get('membership/available', { headers: optionalAuthHeader() });
  if (!data?.data || !Array.isArray(data.data)) return [];
  return data.data.flatMap(group => {
    const choices = group.membershipchoices;
    if (!choices) return [];
    const arr = typeof choices === 'string' ? JSON.parse(choices) : choices;
    return arr.map(c => ({ ...c, showusd: group.showusd }));
  });
}

export async function createOrderSubscription({ membershipId, currency = 'INR', name = '', email = '', contact = '' }) {
  const { data } = await api.post('membership/order',
    { membershipid: membershipId, currency, name, email, contact },
    { headers: authHeader() }
  );
  return data?.data ?? data;
}

export async function updateOrderSubscription({ orderId, paymentId, signature, status = 'Success', errorcode = null, errortext = '' }) {
  const { data } = await api.put('membership/order',
    { status, paymentId, orderId, signature, errorcode, errortext },
    { headers: authHeader() }
  );
  return data;
}

export async function removeWatchHistory() {
  const { data } = await api.delete('continuereset/mobile/all', { headers: authHeader() });
  return data;
}

// ── Resume position ───────────────────────────────────────────────────────────
// GET /movie/mobile/resume/:filmid/:episodeid?
// Returns the last watched position in SECONDS (number) from watchhistoryvw.
// Only returns >0 if the film/episode is in progress (completed=0).
export async function fetchResumePosition(filmId, episodeId = null) {
  if (!_authToken) return 0;
  const path = episodeId
    ? `movie/mobile/resume/${filmId}/${episodeId}`
    : `movie/mobile/resume/${filmId}`;
  const { data } = await api.get(path, { headers: authHeader() });
  // API returns { valid: true, data: <seconds> }
  const secs = data?.data;
  return typeof secs === 'number' && secs > 10 ? secs : 0;
}

export async function logout({ deviceId, platform = 'web' } = {}) {
  const { data } = await api.post('device/logoff', null, {
    headers: { ...authHeader(), ...(deviceId && { deviceid: deviceId }), platform },
  });
  return data;
}

export async function fetchWatchHistory() {
  const { data } = await api.get('activity/history/mobile', { headers: authHeader() });
  return data;
}

export async function requestVideoAuth(filmId) {
  const base = process.env.REACT_APP_API_BASE_URL.replace('/v3.0', '');
  const res = await fetch(`${base}/v3.0/video-auth/${filmId}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      apikey:        process.env.REACT_APP_API_KEY,
      appversion:    process.env.REACT_APP_VERSION,
      authorization: _authToken || '',
    },
  });
  if (!res.ok) throw new Error(`video-auth failed: ${res.status}`);
  return res.json();
}

export default api;
